import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap, useMapEvents } from 'react-leaflet';
import Control from 'react-leaflet-custom-control';
import { LatLngTuple, latLngBounds } from 'leaflet';
import { Button, Tooltip } from '@mui/material';
import { AspectRatio } from '@mui/icons-material';

import { isValidLatLong } from '@/utilities/latlong';
import { ctxRoutes } from '@/contexts/RoutesCtx';
import { MinimalStatusCollection } from '@/generated/model';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

export function RoutesBoundaryComponent({ fullRoutes }: { fullRoutes: MinimalStatusCollection[] | undefined }) {
  const { t } = useTranslation();
  const [autoAdjust, setAutoAdjust] = useState<boolean>(true);
  const map = useMap();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const routeCtx = useContext(ctxRoutes);

  function getPositionsFromRoutes(fullRoutes: MinimalStatusCollection[] | undefined) {
    if (!fullRoutes) return undefined;
    return fullRoutes
      .flatMap((p) => p.positions)
      .filter((p) => isValidLatLong(p))
      .map((p) => [p.latitude, p.longitude] as LatLngTuple);
  }

  useMapEvents({
    resize: () => {
      if (autoAdjust || routeCtx?.adjustMap) setBoundaries();
    },
    dragstart: () => {
      if (!fullRoutes) {
        return;
      }
      setAutoAdjust(false);
      routeCtx?.setAdjustMap(false);
    },
    zoomend: () => {
      if (!fullRoutes) {
        return;
      }
      try {
        const positions = getPositionsFromRoutes(fullRoutes);
        if (!positions || positions.length === 0) {
          return;
        }
        const mapBounds = map.getBounds();
        if (!mapBounds.isValid()) {
          return;
        }
        const positionsBounds = latLngBounds(positions);
        if (!positionsBounds.isValid()) {
          return;
        }

        if (!mapBounds.contains(positionsBounds)) {
          setAutoAdjust(false);
          routeCtx?.setAdjustMap(false);
          return;
        }
      } catch (e) {
        const targetId = fullRoutes[0].target.id;
        const routesLength = fullRoutes.length;
        console.error('Error during zoomend for target %s with %s routes', targetId, routesLength, e);
      }
    },
  });

  useEffect(() => {
    if (!map || (!autoAdjust && !routeCtx?.adjustMap)) return;
    // debounce
    let timer: NodeJS.Timeout | undefined = undefined;
    resizeObserverRef.current = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          map.invalidateSize();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
          // ezzel nem tudunk mit csinalni...
        }
      }, 100);
    });
    resizeObserverRef.current.observe(map.getContainer());

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [map, autoAdjust, routeCtx?.adjustMap]);

  const setBoundaries = useCallback(() => {
    if (!fullRoutes || (!autoAdjust && !routeCtx?.adjustMap)) {
      return;
    }
    try {
      const positions = getPositionsFromRoutes(fullRoutes);
      if (!positions || positions.length === 0) {
        return;
      }
      const bounds = latLngBounds(positions);
      map.fitBounds(bounds, {
        padding: [8, 8],
        animate: false,
      });
    } catch (e) {
      console.log(e);
    }
  }, [fullRoutes, map, autoAdjust, routeCtx?.adjustMap]);

  useEffect(() => {
    if (routeCtx?.adjustMap) {
      setAutoAdjust(true);
    }
  }, [routeCtx?.adjustMap]);

  useEffect(() => {
    setBoundaries();
  }, [fullRoutes, setBoundaries]);

  useEffect(() => {
    if (autoAdjust) {
      setBoundaries();
    }
  }, [autoAdjust]);

  useEffect(() => {
    if (routeCtx?.highlightedRoutes?.length) {
      setAutoAdjust(true);
    }
  }, [routeCtx?.highlightedRoutes]);

  useEffect(() => {
    if (!map) return;
    const zoomInBtn = document.querySelector('.leaflet-control-zoom-in');
    const zoomOutBtn = document.querySelector('.leaflet-control-zoom-out');

    if (zoomInBtn) {
      zoomInBtn.setAttribute('data-qa-element-id', 'map_zoom_in_button');
    }
    if (zoomOutBtn) {
      zoomOutBtn.setAttribute('data-qa-element-id', 'map_zoom_out_button');
    }
  }, [map]);

  return (
    <Control prepend position="bottomleft">
      <Tooltip placement={isPhoneOrHighScreen() ? 'left' : 'bottom'} title={t('map.controllButtons.fitMap')}>
        <Button
          className={`map-boundary-button ${autoAdjust ? 'active' : 'inactive'}`}
          onClick={() => {
            setAutoAdjust((prev) => !prev);
            routeCtx?.setAdjustMap((prev) => !prev);
          }}
          sx={{ bottom: 16 }}
          data-qa-element-id="align_map_button"
        >
          <AspectRatio />
        </Button>
      </Tooltip>
    </Control>
  );
}
